import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  EvaluationFormationRow,
  EvalType,
  FormateurDispoRow,
  FormateurRow,
  FormateurStatut,
  FormationRow,
  InscriptionFormationRow,
  InscriptionStatut,
  SalleFormationRow,
  SessionFormationRow,
  SessionStatut,
  StatsFormationRow,
} from '@/lib/database.types';

// ===== Formations =====
export function useFormations(filter: {
  entrepriseId?: string | null;
  sousSegmentId?: string | null;
  sousSegmentIds?: string[] | null;
  statut?: 'actif' | 'archive' | 'draft' | null;
  search?: string;
}) {
  return useQuery({
    queryKey: ['formations', filter],
    queryFn: async (): Promise<FormationRow[]> => {
      let q = supabase.from('formations').select('*').order('titre');
      if (filter.entrepriseId) q = q.eq('entreprise_id', filter.entrepriseId);
      if (filter.sousSegmentId) q = q.eq('sous_segment_id', filter.sousSegmentId);
      if (filter.sousSegmentIds && filter.sousSegmentIds.length > 0)
        q = q.in('sous_segment_id', filter.sousSegmentIds);
      if (filter.statut) q = q.eq('statut', filter.statut);
      if (filter.search && filter.search.length >= 2) {
        const s = filter.search.replace(/%/g, '');
        q = q.or(`titre.ilike.%${s}%,code.ilike.%${s}%`);
      }
      const { data, error } = await q.limit(200);
      if (error) throw error;
      return data as unknown as FormationRow[];
    },
  });
}

export function useFormation(id: string | null) {
  return useQuery({
    queryKey: ['formation', id],
    enabled: !!id,
    queryFn: async (): Promise<FormationRow | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('formations')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as FormationRow | null) ?? null;
    },
  });
}

export function useUpsertFormation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Partial<FormationRow> & { entreprise_id: string; titre: string; code: string },
    ): Promise<FormationRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('formations')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as FormationRow;
      }
      const { data, error } = await supabase
        .from('formations')
        .insert(rest as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as FormationRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['formations'] });
      void qc.invalidateQueries({ queryKey: ['formation'] });
    },
  });
}

export function useDeleteFormation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('formations').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['formations'] }),
  });
}

// ===== Bulk formations =====
export interface BulkResult {
  success: number;
  errors: string[];
}

export function useBulkFormations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      ids: string[];
      action: 'archive' | 'unarchive' | 'delete' | 'change_segment' | 'change_formateur';
      sous_segment_id?: string | null;
      formateur_id?: string | null;
    }): Promise<BulkResult> => {
      if (input.ids.length === 0) return { success: 0, errors: [] };
      if (input.action === 'delete') {
        const { error, count } = await supabase
          .from('formations')
          .delete({ count: 'exact' })
          .in('id', input.ids);
        if (error) return { success: 0, errors: [error.message] };
        return { success: count ?? 0, errors: [] };
      }
      const patch: Partial<FormationRow> = {};
      if (input.action === 'archive') patch.statut = 'archive';
      else if (input.action === 'unarchive') patch.statut = 'actif';
      else if (input.action === 'change_segment')
        patch.sous_segment_id = input.sous_segment_id ?? null;
      else if (input.action === 'change_formateur')
        patch.formateur_principal_id = input.formateur_id ?? null;
      const { error, count } = await supabase
        .from('formations')
        .update(patch as never, { count: 'exact' })
        .in('id', input.ids);
      if (error) return { success: 0, errors: [error.message] };
      return { success: count ?? input.ids.length, errors: [] };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['formations'] }),
  });
}

// ===== Formateurs =====
export function useFormateurs() {
  return useQuery({
    queryKey: ['formateurs'],
    queryFn: async (): Promise<FormateurRow[]> => {
      const { data, error } = await supabase.from('formateurs').select('*').order('nom');
      if (error) throw error;
      return data as unknown as FormateurRow[];
    },
  });
}

export function useUpsertFormateur() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Partial<FormateurRow> & { nom: string; prenom: string },
    ): Promise<FormateurRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('formateurs')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as FormateurRow;
      }
      const { data, error } = await supabase
        .from('formateurs')
        .insert(rest as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as FormateurRow;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['formateurs'] }),
  });
}

export function useFormateurDispos(formateurId: string | null) {
  return useQuery({
    queryKey: ['formateur_dispos', formateurId],
    enabled: !!formateurId,
    queryFn: async (): Promise<FormateurDispoRow[]> => {
      if (!formateurId) return [];
      const { data, error } = await supabase
        .from('formateurs_disponibilites')
        .select('*')
        .eq('formateur_id', formateurId)
        .order('date_debut');
      if (error) throw error;
      return data as unknown as FormateurDispoRow[];
    },
  });
}

// ===== Sessions =====
export function useSessions(filter: {
  formationId?: string | null;
  formateurId?: string | null;
  statut?: SessionStatut | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}) {
  return useQuery({
    queryKey: ['sessions_formation', filter],
    queryFn: async (): Promise<SessionFormationRow[]> => {
      let q = supabase
        .from('sessions_formation')
        .select('*')
        .order('date_debut', { ascending: true });
      if (filter.formationId) q = q.eq('formation_id', filter.formationId);
      if (filter.formateurId) q = q.eq('formateur_principal_id', filter.formateurId);
      if (filter.statut) q = q.eq('statut', filter.statut);
      if (filter.dateFrom) q = q.gte('date_debut', filter.dateFrom);
      if (filter.dateTo) q = q.lte('date_fin', filter.dateTo);
      const { data, error } = await q.limit(500);
      if (error) throw error;
      return data as unknown as SessionFormationRow[];
    },
  });
}

export function useSession(id: string | null) {
  return useQuery({
    queryKey: ['session_formation', id],
    enabled: !!id,
    queryFn: async (): Promise<SessionFormationRow | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('sessions_formation')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as SessionFormationRow | null) ?? null;
    },
  });
}

export function useUpsertSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Partial<SessionFormationRow> & {
        formation_id: string;
        date_debut: string;
        date_fin: string;
      },
    ): Promise<SessionFormationRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('sessions_formation')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as SessionFormationRow;
      }
      const { data, error } = await supabase
        .from('sessions_formation')
        .insert(rest as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as SessionFormationRow;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sessions_formation'] }),
  });
}

// ===== Inscriptions =====
export function useInscriptions(filter: {
  sessionId?: string | null;
  tierId?: string | null;
  opportuniteId?: string | null;
  commercialId?: string | null;
  statut?: InscriptionStatut | null;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}) {
  const pageSize = filter.pageSize ?? 50;
  const page = filter.page ?? 0;
  return useQuery({
    queryKey: ['inscriptions_formation', filter],
    enabled: filter.enabled !== false,
    queryFn: async (): Promise<{ rows: InscriptionFormationRow[]; count: number }> => {
      let q = supabase
        .from('inscriptions_formation')
        .select('*', { count: 'exact' })
        .order('date_inscription', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);
      if (filter.sessionId) q = q.eq('session_id', filter.sessionId);
      if (filter.tierId) q = q.eq('tier_id', filter.tierId);
      if (filter.opportuniteId) q = q.eq('opportunite_id', filter.opportuniteId);
      if (filter.commercialId) q = q.eq('commercial_id', filter.commercialId);
      if (filter.statut) q = q.eq('statut', filter.statut);
      const { data, error, count } = await q;
      if (error) throw error;
      return { rows: data as unknown as InscriptionFormationRow[], count: count ?? 0 };
    },
    placeholderData: (prev) => prev,
  });
}

export function useUpsertInscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Partial<InscriptionFormationRow> & {
        session_id: string;
        tier_id: string;
        prix_facture: number;
      },
    ): Promise<InscriptionFormationRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('inscriptions_formation')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as InscriptionFormationRow;
      }
      const { data, error } = await supabase
        .from('inscriptions_formation')
        .insert(rest as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as InscriptionFormationRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inscriptions_formation'] });
      void qc.invalidateQueries({ queryKey: ['sessions_formation'] });
    },
  });
}

// ===== Évaluations =====
export function useEvaluations(filter: { sessionId?: string | null; type?: EvalType | null }) {
  return useQuery({
    queryKey: ['evaluations', filter],
    queryFn: async (): Promise<EvaluationFormationRow[]> => {
      let q = supabase
        .from('evaluations_formation')
        .select('*')
        .order('completed_at', { ascending: false, nullsFirst: false });
      if (filter.sessionId) q = q.eq('session_id', filter.sessionId);
      if (filter.type) q = q.eq('type', filter.type);
      const { data, error } = await q.limit(500);
      if (error) throw error;
      return data as unknown as EvaluationFormationRow[];
    },
  });
}

// ===== Stats =====
export function useStatsFormations(entrepriseId: string | null) {
  return useQuery({
    queryKey: ['stats_formations', entrepriseId],
    enabled: !!entrepriseId,
    queryFn: async (): Promise<StatsFormationRow[]> => {
      if (!entrepriseId) return [];
      const { data, error } = await supabase
        .from('v_stats_formations')
        .select('*')
        .eq('entreprise_id', entrepriseId);
      if (error) throw error;
      return (data ?? []) as unknown as StatsFormationRow[];
    },
  });
}

// ===== Salles =====
export function useSalles(entrepriseId: string | null) {
  return useQuery({
    queryKey: ['salles_formation', entrepriseId],
    enabled: !!entrepriseId,
    queryFn: async (): Promise<SalleFormationRow[]> => {
      if (!entrepriseId) return [];
      const { data, error } = await supabase
        .from('salles_formation')
        .select('*')
        .eq('entreprise_id', entrepriseId)
        .order('nom');
      if (error) throw error;
      return data as unknown as SalleFormationRow[];
    },
  });
}

// ===== Présences =====
export function usePresences(sessionId: string | null) {
  return useQuery({
    queryKey: ['presences_formation', sessionId],
    enabled: !!sessionId,
    queryFn: async () => {
      if (!sessionId) return [];
      const { data, error } = await supabase
        .from('presences_formation')
        .select('*')
        .eq('session_id', sessionId);
      if (error) throw error;
      return data;
    },
  });
}

export function useUpsertPresence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      inscription_id: string;
      session_id: string;
      jour: string;
      present: boolean;
      heure_arrivee?: string | null;
      heure_depart?: string | null;
      signature_url?: string | null;
      motif_absence?: string | null;
    }) => {
      const { error } = await supabase.from('presences_formation').upsert(input, {
        onConflict: 'inscription_id,jour',
      });
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['presences_formation', vars.session_id] });
    },
  });
}

// ===== Bulk présences =====
export function useBulkPresences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      session_id: string;
      jour: string;
      inscription_ids: string[];
      present: boolean;
    }): Promise<BulkResult> => {
      if (input.inscription_ids.length === 0) return { success: 0, errors: [] };
      const records = input.inscription_ids.map((iid) => ({
        inscription_id: iid,
        session_id: input.session_id,
        jour: input.jour,
        present: input.present,
      }));
      const { error } = await supabase
        .from('presences_formation')
        .upsert(records, { onConflict: 'inscription_id,jour' });
      if (error) return { success: 0, errors: [error.message] };
      return { success: records.length, errors: [] };
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['presences_formation', vars.session_id] });
    },
  });
}

// ===== Bulk sessions =====
export function useBulkSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      ids: string[];
      action: 'annuler' | 'cloturer' | 'reporter' | 'change_formateur' | 'delete';
      decalage_jours?: number;
      formateur_id?: string | null;
    }): Promise<BulkResult> => {
      if (input.ids.length === 0) return { success: 0, errors: [] };
      if (input.action === 'delete') {
        const { error, count } = await supabase
          .from('sessions_formation')
          .delete({ count: 'exact' })
          .in('id', input.ids);
        if (error) return { success: 0, errors: [error.message] };
        return { success: count ?? 0, errors: [] };
      }
      if (input.action === 'reporter') {
        // Récupérer les sessions, recalculer dates puis update une par une (PG ne supporte pas
        // un UPDATE arithmétique en bulk via supabase-js, on utilise une RPC simple).
        const { data: sessions } = await supabase
          .from('sessions_formation')
          .select('id, date_debut, date_fin')
          .in('id', input.ids);
        if (!sessions) return { success: 0, errors: ['Erreur récupération sessions'] };
        const errs: string[] = [];
        let ok = 0;
        const delta = input.decalage_jours ?? 7;
        for (const s of sessions) {
          const nd = new Date(s.date_debut);
          nd.setDate(nd.getDate() + delta);
          const nf = new Date(s.date_fin);
          nf.setDate(nf.getDate() + delta);
          const { error } = await supabase
            .from('sessions_formation')
            .update({
              date_debut: nd.toISOString().slice(0, 10),
              date_fin: nf.toISOString().slice(0, 10),
              statut: 'reportee',
            })
            .eq('id', s.id);
          if (error) errs.push(error.message);
          else ok++;
        }
        return { success: ok, errors: errs.slice(0, 3) };
      }
      const patch: Partial<SessionFormationRow> = {};
      if (input.action === 'annuler') patch.statut = 'annulee';
      else if (input.action === 'cloturer') patch.statut = 'terminee';
      else if (input.action === 'change_formateur')
        patch.formateur_principal_id = input.formateur_id ?? null;
      const { error, count } = await supabase
        .from('sessions_formation')
        .update(patch as never, { count: 'exact' })
        .in('id', input.ids);
      if (error) return { success: 0, errors: [error.message] };
      return { success: count ?? input.ids.length, errors: [] };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sessions_formation'] });
    },
  });
}

// ===== Bulk inscriptions =====
export function useBulkInscriptions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      ids: string[];
      action: 'annuler' | 'confirmer' | 'change_commercial' | 'delete';
      commercial_id?: string | null;
    }): Promise<BulkResult> => {
      if (input.ids.length === 0) return { success: 0, errors: [] };
      if (input.action === 'delete') {
        const { error, count } = await supabase
          .from('inscriptions_formation')
          .delete({ count: 'exact' })
          .in('id', input.ids);
        if (error) return { success: 0, errors: [error.message] };
        return { success: count ?? 0, errors: [] };
      }
      const patch: Partial<InscriptionFormationRow> = {};
      if (input.action === 'annuler') patch.statut = 'annule';
      else if (input.action === 'confirmer') patch.statut = 'confirme';
      else if (input.action === 'change_commercial')
        patch.commercial_id = input.commercial_id ?? null;
      const { error, count } = await supabase
        .from('inscriptions_formation')
        .update(patch as never, { count: 'exact' })
        .in('id', input.ids);
      if (error) return { success: 0, errors: [error.message] };
      return { success: count ?? input.ids.length, errors: [] };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['inscriptions_formation'] }),
  });
}

// ===== Bulk évaluations =====
export function useBulkEvaluations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      ids: string[];
      action: 'regenerer' | 'delete';
    }): Promise<BulkResult> => {
      if (input.ids.length === 0) return { success: 0, errors: [] };
      if (input.action === 'delete') {
        const { error, count } = await supabase
          .from('evaluations_formation')
          .delete({ count: 'exact' })
          .in('id', input.ids);
        if (error) return { success: 0, errors: [error.message] };
        return { success: count ?? 0, errors: [] };
      }
      // regenerer : prolonge token_expire_at de 7j
      const newExpire = new Date(Date.now() + 7 * 86400000).toISOString();
      const { error, count } = await supabase
        .from('evaluations_formation')
        .update({ token_expire_at: newExpire }, { count: 'exact' })
        .in('id', input.ids)
        .is('completed_at', null);
      if (error) return { success: 0, errors: [error.message] };
      return { success: count ?? 0, errors: [] };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['evaluations'] }),
  });
}

/** Crée des évaluations à chaud en masse pour toutes les inscriptions actives d'une session.
 *  Si `envoyer=true`, les tokens sont créés. Si `envoyer=false`, les évaluations sont créées
 *  en brouillon (token_expire_at = epoch + 1, donc invisible aux participants jusqu'à régénération). */
export function useCreateEvaluationsForSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      session_id: string;
      type: 'chaud' | 'froid';
      envoyer: boolean;
    }): Promise<BulkResult> => {
      // Récupérer les inscriptions actives de cette session
      const { data: insc, error: e1 } = await supabase
        .from('inscriptions_formation')
        .select('id')
        .eq('session_id', input.session_id)
        .in('statut', ['confirme', 'paye_partiel', 'paye_total']);
      if (e1) return { success: 0, errors: [e1.message] };
      if (!insc || insc.length === 0) return { success: 0, errors: ['Aucune inscription active'] };

      // Vérifier les évaluations déjà existantes pour ces inscriptions+type
      const inscIds = insc.map((i) => (i as { id: string }).id);
      const { data: existing } = await supabase
        .from('evaluations_formation')
        .select('inscription_id')
        .in('inscription_id', inscIds)
        .eq('type', input.type);
      const existingSet = new Set(
        (existing ?? []).map((e) => (e as { inscription_id: string }).inscription_id),
      );

      // Créer les évaluations manquantes
      const expireAt = input.envoyer
        ? new Date(Date.now() + 7 * 86400000).toISOString()
        : new Date(0).toISOString(); // brouillon : token expiré → invisible aux participants

      const toCreate = inscIds
        .filter((iid) => !existingSet.has(iid))
        .map((iid) => ({
          session_id: input.session_id,
          inscription_id: iid,
          type: input.type,
          token: `eval_${crypto.randomUUID().replace(/-/g, '')}`,
          token_expire_at: expireAt,
        }));

      if (toCreate.length === 0)
        return { success: 0, errors: ['Toutes les évaluations existent déjà'] };

      const { error, count } = await supabase
        .from('evaluations_formation')
        .insert(toCreate, { count: 'exact' });
      if (error) return { success: 0, errors: [error.message] };
      return { success: count ?? toCreate.length, errors: [] };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['evaluations'] }),
  });
}

// ===== Bulk formateurs =====
export function useBulkFormateurs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      ids: string[];
      action: 'archive' | 'unarchive' | 'change_statut' | 'delete';
      statut?: FormateurStatut;
    }): Promise<BulkResult> => {
      if (input.ids.length === 0) return { success: 0, errors: [] };
      if (input.action === 'delete') {
        const { error, count } = await supabase
          .from('formateurs')
          .delete({ count: 'exact' })
          .in('id', input.ids);
        if (error) return { success: 0, errors: [error.message] };
        return { success: count ?? 0, errors: [] };
      }
      const patch: Partial<FormateurRow> = {};
      if (input.action === 'archive') patch.is_active = false;
      else if (input.action === 'unarchive') patch.is_active = true;
      else if (input.action === 'change_statut' && input.statut) patch.statut = input.statut;
      const { error, count } = await supabase
        .from('formateurs')
        .update(patch as never, { count: 'exact' })
        .in('id', input.ids);
      if (error) return { success: 0, errors: [error.message] };
      return { success: count ?? input.ids.length, errors: [] };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['formateurs'] }),
  });
}

// ===== Helper export CSV =====
export function exportToCSV<T extends Record<string, unknown>>(rows: T[], filename: string): void {
  if (rows.length === 0) return;
  const firstRow = rows[0];
  if (!firstRow) return;
  const headers = Object.keys(firstRow);
  const csv = [
    headers.join(','),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const v = r[h];
          const s = v == null ? '' : String(v);
          return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(','),
    ),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
